import Container from "../Container/Container";
import s from "./Footer.module.scss";

import { useClinicInfo } from "../../../api/queries/useClinicInfo";
import { toMap } from "../../../app/clinicInfoMap";

import { Link, NavLink, useLocation } from "react-router-dom";
import { CSSProperties, useMemo } from "react";

type NavItem =
  | { label: string; to: string; end?: boolean }
  | { label: string; href: string };

type SocialItem = {
  key: string;
  href: string;
  label: string;
  iconSrc: string;
};

function getPhones(cfg: Record<string, string | undefined>) {
  const phones: string[] = [];

  const raw = cfg["contacts.phones"];
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((x) => {
          if (typeof x === "string" && x.trim()) {
            phones.push(x.trim());
          }
        });
      }
    } catch {
      // ignore
    }
  }

  if (phones.length === 0 && cfg["contacts.phone"]?.trim()) {
    phones.push(cfg["contacts.phone"]!.trim());
  }

  if (phones.length === 0) {
    phones.push("+7 (000) 000-00-00");
  }

  return Array.from(new Set(phones));
}

function normalizeExternalLink(href: string) {
  const trimmed = href.trim();
  if (!trimmed) return "";

  if (/^(https?:)?\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed.replace(/^\/+/, "")}`;
}

export default function Footer() {
  const { data, isLoading, isError } = useClinicInfo();
  const cfg = toMap(data ?? []) as Record<string, string | undefined>;
  const location = useLocation();

  const phones = getPhones(cfg);
  const address = cfg["contacts.address"] || "Адрес будет позже";
  const hours = cfg["contacts.hours"] || "График будет позже";

  const about =
    (cfg["footer.about"] || "").trim() ||
    "Короткое описание. Здесь будет текст, который потом заменим данными из clinic-info.";

  const toTel = (p: string) => p.replace(/[^\d+]/g, "");

  const socialItems: SocialItem[] = [
    {
      key: "vk",
      href: (cfg["contacts.social.vk"] || "").trim(),
      label: "VK",
      iconSrc: "/icons/vk.svg",
    },
    {
      key: "max",
      href: (cfg["contacts.social.max"] || "").trim(),
      label: "MAX",
      iconSrc: "/icons/max.svg",
    },
  ].filter((item) => Boolean(item.href));

  const navItems: NavItem[] = useMemo(
    () => [
      { label: "Главная", to: "/", end: true },
      { label: "Услуги", to: "/services" },
      { label: "Цены", to: "/price" },
      { label: "Акции", to: "/blog" },
      { label: "Команда", to: "/team" },
      { label: "Контакты", to: "/contacts" },
      { label: "Правовая информация", to: "/document" },
      { label: "Политика конфиденциальности", to: "/privacy-policy" },
    ],
    []
  );

  const anchorTo = (href: string) => {
    if (location.pathname === "/") return href;
    return `/${href}`;
  };

  const renderFooterLink = (item: NavItem) => {
    if ("to" in item) {
      return (
        <NavLink
          key={item.label}
          to={item.to}
          end={item.end}
          className={({ isActive }) => (isActive ? s.linkActive : s.link)}
        >
          {item.label}
        </NavLink>
      );
    }

    return (
      <Link key={item.label} to={anchorTo(item.href)} className={s.link}>
        {item.label}
      </Link>
    );
  };

  const getIconMaskStyle = (iconSrc: string): CSSProperties => ({
    WebkitMaskImage: `url(${iconSrc})`,
    maskImage: `url(${iconSrc})`,
  });

  return (
    <footer className={s.footer} id="contacts">
      <Container className={s.footerContainer}>
        <div className={s.grid}>
          <div>
            <div className={s.h}>Контакты</div>

            {isLoading ? (
              <div className={s.p}>Загрузка…</div>
            ) : isError ? (
              <div className={s.p}>Нет связи с сервером</div>
            ) : (
              <>
                <div className={s.p}>
                  Телефон:
                  {phones.map((p) => (
                    <div key={p}>
                      <a className={s.phoneLink} href={`tel:${toTel(p)}`}>
                        {p}
                      </a>
                    </div>
                  ))}
                </div>

                <div className={s.p}>Адрес: {address}</div>
                <div className={s.p}>Режим: {hours}</div>

                {socialItems.length > 0 && (
                  <div className={s.socials} aria-label="Социальные сети">
                    {socialItems.map((item) => (
                      <a
                        key={item.key}
                        href={normalizeExternalLink(item.href)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={item.label}
                        title={item.label}
                        className={s.socialLink}
                      >
                        <span
                          className={`${s.socialIcon} ${s[`socialIcon_${item.key}`]}`}
                          style={getIconMaskStyle(item.iconSrc)}
                          aria-hidden="true"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <div className={s.h}>Навигация</div>
            <div className={s.navList}>{navItems.map(renderFooterLink)}</div>
          </div>

          <div>
            <div className={s.h}>О клинике</div>
            <div className={s.p}>{about}</div>
          </div>
        </div>

        <div className={s.bottom}>
          <span>© {new Date().getFullYear()} Ю-ДЕНТ</span>
          <span className={s.muted}>Design by CodeTwins</span>
        </div>
      </Container>
    </footer>
  );
}
